import { GoogleMap, Marker } from '@react-google-maps/api';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ReactComponent as ClockIcon } from '../../assets/icons/clock-v2-icon.svg';
import { ReactComponent as DeleteIcon } from '../../assets/icons/delete-icon.svg';
import { ReactComponent as EditIcon } from '../../assets/icons/edit.svg';
import { ReactComponent as NewTabIcon } from '../../assets/icons/tables/new-tab.svg';
import { useAppBranding } from '../../config/branding';
import DefaultApiLimit from '../../config/defaultLimit';
import { useGoogleMaps } from '../../contexts/google-map-context/GoogleMapContent';
import { FacilityDetailsInterface } from '../../interface/station-management.interface';
import EditFacilityTagComponent from '../../pages/facility-tag/components/EditFacilityTag';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
// import { fetchAllStationsList } from '../../redux/slice/ApprovedStationManagementSlice';
import { fetchFacilityTags } from '../../redux/slice/FacilityTagSlice';
import { setNotificationDetails } from '../../redux/slice/NotificationSlice';
import { GetParametersFromCommonFilters } from '../../shared-functions/CommonFilters';
import ConvertUTCtoHHMM from '../../shared-functions/ConvertUTCtoHHMM';
import { FormatUtcString } from '../../shared-functions/FormatUtcString';
import facilityTagApi from '../../utils/api/facility-tag';
import chargingStation from '../../utils/api/station';
import BatteryLoader from '../battery-loader/BatteryLoader';
import DeleteWarning from '../delete-warning/DeleteWarning';
import ModalBox from '../modal-box/ModalBox';
import SuccessNotification from '../success-notification/SuccessNotification';

interface StationSidePanelProperties {
  fetchIdFrom: 'queryParams' | 'props';
  id?: string;
  currentData?: any;
  isEditMode?: boolean;
  onEditModeChange?: (state: boolean) => void;
}

interface ModalBoxDetail {
  title: string | null;
  header: boolean;
  content: JSX.Element | null;
  notificationModal: boolean;
}

/** Validate lat lng */
const validatePosition = (lat: any, lng: any): google.maps.LatLngLiteral | null => {
  const latitude = parseFloat(lat);
  const longitude = parseFloat(lng);

  if (Number.isNaN(latitude) || Number.isNaN(longitude)) return null;

  return { lat: latitude, lng: longitude };
};

/**
 * Station side panel
 * @param properties
 * @returns
 */
function FacilitySidePanel(properties: StationSidePanelProperties) {
  const { fetchIdFrom, id, currentData } = properties;
  const branding = useAppBranding();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isLoaded } = useGoogleMaps();
  const [isEdit, setIsEdit] = useState(false);
  const { t } = useTranslation('tableSidepanel');
  const [isLoader, setIsLoader] = useState<boolean>(false);
  const { filters, sortField, sortBy } = useAppSelector((state) => state.facilityTags);
  const [searchParameters, setSearchParameters] = useSearchParams();
  const [facilityData, setFacilityData] = useState<FacilityDetailsInterface | null>(null);
  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    header: false,
    content: null,
    notificationModal: false,
  });

  /**
   * Function to fetch station details
   * @param facilityId
   */
  const handleFetchFacilityDetails = async (facilityId: string) => {
    chargingStation
      .fetchFacilityDetails(facilityId)
      .then((response) => {
        setFacilityData(response?.data?.[0]);
        setIsLoader(false);
      })
      .catch((error) => {
        setIsLoader(false);
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      });
  };

  /**
   * Function  /**
   * Handle the delete facility tag
   * @param facilityId - id of the facility tag
   */
  const handleDeleteFacilityTag = (facilityId: string) => {
    setIsLoader(true);
    facilityTagApi
      .deleteFacilityTag(facilityId)
      .then(() => {
        setIsLoader(false);
        const newParameters = new URLSearchParams(searchParameters);
        newParameters.delete('facilityId');
        setSearchParameters(newParameters);
        const queryParameters = GetParametersFromCommonFilters(filters);
        console.log(queryParameters);
        dispatch(
          fetchFacilityTags({
            ...queryParameters,
            sortField,
            sortBy,
            page: DefaultApiLimit.page,
            limit: DefaultApiLimit.limit,
          }),
        );
        setModalBoxStatus('open');
        setModalBoxDetail({
          title: null,
          header: false,
          content: (
            <SuccessNotification
              title={t('facilityDeleted')}
              description={t('successDesc')}
              notificationCloseTimer={3000}
              onNotificationClose={() => setModalBoxStatus('close')}
            />
          ),
          notificationModal: true,
        });
      })
      .catch((error) => {
        setIsLoader(false);
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      });
  };

  /**
   * Handle the close panel action
   */
  const handleClosePanel = () => {
    const newParameters = new URLSearchParams(searchParameters);
    newParameters.delete('facilityId');
    setSearchParameters(newParameters);
    setIsEdit(false);
  };

  /**
   * Handle the delete action
   */
  const handleDeleteAction = () => {
    setModalBoxStatus('open');
    setModalBoxDetail({
      title: null,
      header: false,
      content: (
        <DeleteWarning
          selectedRowId={currentData?._id || ''}
          title={t('areYouSureDelete')}
          description={t('deleteDescription')}
          cancelButtonOnClick={() => setModalBoxStatus('close')}
          submitSvgIcon={<DeleteIcon />}
          submitButtonOnClick={(facilityId?: string | null) => {
            if (!facilityId) return;
            handleDeleteFacilityTag(facilityId);
          }}
        />
      ),
      notificationModal: true,
    });
  };

  /**
   * Handle the open station management
   */
  const handleOpenStationManagement = (stationId: string, stationName: string) => {
    navigate(
      `/ev-admin/facility-management/station/${stationId}/station-dashboard?searchBy=${stationName}`,
    );
  };

  useEffect(() => {
    setIsLoader(true);
    const facilityId = fetchIdFrom === 'queryParams' ? searchParameters.get('facilityId') : id;

    if (!facilityId) {
      setFacilityData(null);
      setIsLoader(false);
      return;
    }

    handleFetchFacilityDetails(facilityId);
  }, [searchParameters, id, fetchIdFrom]);

  if (isLoader) {
    return (
      <div className="loader-wrap">
        <div className="loader">
          <BatteryLoader />
        </div>
      </div>
    );
  }

  return (
    <div className="facility-side-panel-wrap">
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        modalBoxAnimation="modalAnimateToLeft"
        notification={modalBoxDetail.notificationModal}
        header={modalBoxDetail.header}
      />
      <div className="facility-side-panel-top">
        <div className="facility-details-container">
          <div className="facility-details-header">
            <p className="title">{isEdit ? t('editFacilityDetails') : t('facilityDetails')}</p>
            {!isEdit && (
              <div className="action-buttons">
                <EditIcon className="icon edit" onClick={() => setIsEdit(true)} />
                <DeleteIcon className="icon delete" onClick={handleDeleteAction} />
              </div>
            )}
          </div>
          {isEdit ? (
            <EditFacilityTagComponent
              handleCancel={() => setIsEdit(false)}
              handleSuccess={handleClosePanel}
              selectedFacilityTag={currentData}
            />
          ) : (
            <div className="station-side-panel-container">
              <div className="station-data-wrapper">
                <div className="station-content-wrap">
                  <div className="station-content-header">
                    <h2 className="station-name">{facilityData?.facilityTagName ?? 'N/A'}</h2>
                    <p className="station-address">{facilityData?.address ?? 'N/A'}</p>
                  </div>
                  <div className="divider" />
                  <div className="station-content">
                    {facilityData?.chargingStationsData?.map((station) => {
                      const validPosition = validatePosition(station.latitude, station.longitude);
                      if (!isLoaded || !validPosition) return null;
                      return (
                        <div className="station-content-container" key={station._id}>
                          <div className="map-wrap">
                            <GoogleMap
                              mapContainerStyle={{ width: '100%', height: '100%' }}
                              center={{
                                lat: Number(station.latitude),
                                lng: Number(station.longitude),
                              }}
                              zoom={12}>
                              {validPosition && (
                                <Marker
                                  position={validPosition}
                                  icon={{
                                    url: branding.icons.stationLocator,
                                    scaledSize: new window.google.maps.Size(44, 44),
                                  }}
                                />
                              )}
                            </GoogleMap>
                          </div>
                          <div key={station._id} className="station-content-item-wrapper">
                            <div className="station-content-item-container">
                              <h3
                                role="presentation"
                                className="station-content-item-title"
                                onClick={() =>
                                  handleOpenStationManagement(station._id, station.name)
                                }>
                                {station.name}
                              </h3>
                              <p className="station-content-item-address">{station.address}</p>
                              <div className="station-status">
                                <div
                                  className={`status ${station.isAvailable ? 'open' : 'closed'} `}>
                                  {station.isAvailable ? t('open') : t('closed')}
                                </div>
                                <span className="time">
                                  <ClockIcon className="clock-icon" />
                                  {t('timeRange', {
                                    start: FormatUtcString(
                                      ConvertUTCtoHHMM(station?.opensAt || ''),
                                    ),
                                    end: FormatUtcString(ConvertUTCtoHHMM(station?.closesAt || '')),
                                  })}
                                </span>
                                <NewTabIcon
                                  className="open-icon"
                                  onClick={() =>
                                    handleOpenStationManagement(station._id, station.name)
                                  }
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default FacilitySidePanel;
